import { githubHostOf, nodeHasCapability, type NodeDTO, type NodeServiceDTO, type ProjectDTO } from "@roomate/core";

/** Shown on the project page while no machine can act on the project's GitHub repo (docs/specs/github-machine-cli.md §5.3). */
export const WAITING_FOR_GITHUB = "Waiting for a machine logged in to GitHub";

/** `gh` login state for one host as a node last reported it (the `github:<host>` capability row), or null before its first report. */
export function githubService(node: Pick<NodeDTO, "services">, host: string): NodeServiceDTO | null {
  return node.services?.find((s) => s.name === "github" && s.scope === host) ?? null;
}

/** Same test the router applies before handing a node a GitHub task: online, signed in, and checked within SERVICE_FRESH_MS. */
export const nodeGithubReady = (node: Pick<NodeDTO, "online" | "services">, host: string, now: number): boolean => nodeHasCapability(node, `github:${host}`, now);

/**
 * Whether this project needs a machine logged in to GitHub (core `requiredCapabilities` rule for code tasks): a PR project the
 * GitHub App does not cover, or one the admin put on the node's PR flow. Remote, local and hub-bound projects never wait on a machine.
 */
export const projectNeedsGithubMachine = (project: Pick<ProjectDTO, "repoUrl" | "pushMode" | "github" | "githubFlow" | "delivery">): boolean =>
  githubHostOf(project.repoUrl) !== null &&
  ((project.pushMode === "pr" && !project.github?.bound) ||
    // The node's PR flow only runs for a PR project or when the person merges (server nodePrFlowOf), never for a plain remote push.
    (project.githubFlow === "node" && (project.pushMode === "pr" || project.delivery?.merge === "owner_pr")));

export type GithubNodeStatus = { node: NodeDTO; service: NodeServiceDTO | null; ready: boolean };

/** Every machine with its GitHub state for `host`; ready machines first, then by name. */
export function githubNodeStatuses(nodes: readonly NodeDTO[], host: string, now: number): GithubNodeStatus[] {
  return nodes
    .map((node) => {
      const service = githubService(node, host);
      return { node, service, ready: nodeGithubReady(node, host, now) };
    })
    .sort((a, b) => Number(b.ready) - Number(a.ready) || a.node.name.localeCompare(b.node.name));
}

/** The machine that owns a project's merge queue: its registered merge-only node, else the node that holds its checkout. */
export function mergingNode(project: Pick<ProjectDTO, "id" | "homeNodeId">, nodes: readonly NodeDTO[]): NodeDTO | null {
  return nodes.find((n) => n.role === "merge_only" && n.mergeProjectIds.includes(project.id)) ?? nodes.find((n) => n.id === project.homeNodeId) ?? null;
}
